import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UtilService, DialogService, NotificationService } from '../../../shared/index';
import { EventsService } from '../../../events/shared/events.service';
import { EventPaymentMethodService } from '../../../events/shared/payment-methods/event-payment-method.service';
import { EventItemPaymentMethod } from '../../../events/shared/payment-methods/event-item-payment-method.model';
import { EventPaymentMethod } from '../../../events/shared/payment-methods/event-payment-method.model';
import { PaymentMethodEnum } from '../../../events/shared/payment-methods/payment-method-enum.model';
import { PaymentMethodFeeType } from '../../../events/shared/payment-methods/payment-method-fee-type.model';
import { AsyncState } from '../../../shared/model/index';
import { TranslationService } from '../../../shared/translation/translation.service';

@Component({
  selector: 'wl-event-settings-payment-methods',
  templateUrl: 'event-settings-payment-methods.component.html',
  styleUrls: ['event-settings-payment-methods.component.css'],
  providers: [EventsService, EventPaymentMethodService],
    standalone: false
})
export class EventSettingsPaymentMethodsComponent implements OnInit {
  @Output() public onPaymentMethodsSaved: EventEmitter<Array<EventPaymentMethod>> = new EventEmitter<Array<EventPaymentMethod>>();

  public _router: Router;
  public _route: ActivatedRoute;
  public _dialogService: DialogService;
  public _utilService: UtilService;
  public _eventService: EventsService;
  public _eventPaymentMethodService: EventPaymentMethodService;

  public paymentMethodEnum: any = PaymentMethodEnum;

  public paymentMethods: Array<EventPaymentMethod>;
  public eventItemPaymentMethods: Array<EventItemPaymentMethod>;
  public eventId: number;
  public loadingEventItemPaymentMethods: boolean;
  public selectedEventPaymentMethod: EventPaymentMethod;
  public feeTypes: Array<PaymentMethodFeeType>;

  public saving: boolean = false;
  public saveError: string = '';
  public loading: AsyncState = new AsyncState();
  public showModal = false;

  public constructor(
    router: Router,
    route: ActivatedRoute,
    dialogService: DialogService,
    utilService: UtilService,
    eventPaymentMethodService: EventPaymentMethodService,
    eventService: EventsService,
    public _notificationService: NotificationService,
    public _translationService: TranslationService
  ) {
    this._router = router;
    this._route = route;
    this._dialogService = dialogService;
    this._utilService = utilService;
    this._eventPaymentMethodService = eventPaymentMethodService;
    this._eventService = eventService;

    this.paymentMethods = new Array<EventPaymentMethod>();
    this.eventItemPaymentMethods = new Array<EventItemPaymentMethod>();
    this.eventId = 0;
    this.loadingEventItemPaymentMethods = false;
    this.selectedEventPaymentMethod = new EventPaymentMethod();
    this.feeTypes = new Array<PaymentMethodFeeType>();
  }

  public newEventItemPaymentMethod = (): void => {};

  public saveEventItemPaymentMethod = (): void => {
    this.saving = true;
    this.saveError = '';

    this._eventPaymentMethodService
      .upsertEventPaymentMethod(this.selectedEventPaymentMethod)
      .then(() => {
        this.saving = false;
        this.showModal = false;
        this.refreshPaymentMethods();
        this._translationService
          .getTitlecase('events.settings.payment-methods.save-success-message')
          .then(translation => this._notificationService.success(translation));
      })
      .catch(error => {
        this.saving = false;
        this.saveError = this._utilService.extractErrorMessage(error);
      });
  };

  public cancel = (): void => {
    this.showModal = false;
  };

  public updateEventPaymentMethodAccepted = (paymentMethod: EventPaymentMethod): void => {
    if (
      this._utilService.isNotNullOrUndefined(paymentMethod.PaymentMethodId) &&
      this._utilService.isNotNullOrUndefined(paymentMethod.Accept)
    ) {
      this._eventPaymentMethodService.updateEventPaymentMethodAccepted(this.eventId, paymentMethod.PaymentMethodId, paymentMethod.Accept);
    }
  };

  /**
   * open the edit payment method window
   * @param paymentMethod
   */
  public editEventItemPaymentMethod(paymentMethod: EventPaymentMethod): void {
    this.showModal = true;
    this.saveError = '';
    this.loadingEventItemPaymentMethods = true;
    this.selectedEventPaymentMethod = JSON.parse(JSON.stringify(paymentMethod));

    let promise: Promise<EventPaymentMethod> = null;

    if (paymentMethod.EventPaymentMethodId > 0)
      promise = this._eventPaymentMethodService.getEventPaymentMethod(paymentMethod.EventPaymentMethodId);
    else
      promise = this._eventPaymentMethodService.getEventPaymentMethods(this.eventId).then((data: Array<EventPaymentMethod>) => {
        let eventPaymentMethod = data
          ? data.find(function(x) {
              return x.PaymentMethodId == paymentMethod.PaymentMethodId;
            })
          : paymentMethod;

        if (!eventPaymentMethod) {
          eventPaymentMethod = paymentMethod;
        }

        return eventPaymentMethod;
      });

    // copy
    promise.then(
      (data: EventPaymentMethod) => {
        this.loadingEventItemPaymentMethods = false;
        this.selectedEventPaymentMethod = JSON.parse(JSON.stringify(data));
      },
      error => {
        this.loadingEventItemPaymentMethods = false;
        this.saveError = this._utilService.extractErrorMessage(error);
      }
    );
  }

  /**
   * when the "Accept" chekcbox in the edit modal is clicked
   * @param event
   */
  public handleAvailableToPublicChange(event: any) {
    // can't change model while in angular's update phase
    setTimeout(() => {
      if (this.selectedEventPaymentMethod.Accept) this.uncheckAllEventItemsForPaymentMethod(this.selectedEventPaymentMethod);
    });
  }

  /**
   * Check all event items in the edit modal if the paymentMethod is set to accept
   * * Does not apply to cash/check payment method
   * @param paymentMethod
   */
  public uncheckAllEventItemsForPaymentMethod(paymentMethod: EventPaymentMethod) {
    if (
      !paymentMethod ||
      !paymentMethod.EventItemPaymentMethods ||
      !paymentMethod.Accept ||
      paymentMethod.PaymentMethodId == PaymentMethodEnum.CashCheck
    )
      return;

    paymentMethod.EventItemPaymentMethods.forEach((eventItemPaymentMethod: EventItemPaymentMethod) => {
      if (eventItemPaymentMethod) eventItemPaymentMethod.Accept = true;
    });
  }

  public refreshPaymentMethods(): void {
    this.loading.busy = true;
    this.loading.message = '';

    this._eventPaymentMethodService
      .getEventPaymentMethods(this.eventId)
      .then((data: Array<EventPaymentMethod>) => {
        this.paymentMethods = data;

        //refresh the list of payment methods on the event itself
        this.onPaymentMethodsSaved.emit(data);

        this.loading.busy = false;
        this.loading.success = true;
      })
      .catch(error => {
        this.loading.busy = false;
        this.loading.message = this._utilService.extractErrorMessage(error);
        this.loading.success = false;
      });
  }

  ngOnInit() {
    this._route.params.subscribe(params => {
      if (this._utilService.isNumeric(params['id'])) {
        let id = Number(params['id']);
        this.eventId = id;

        this.refreshPaymentMethods();

        this.feeTypes = this._eventPaymentMethodService.getPaymentMethodFeeTypes();
      }
    });
  }
}
